import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useStoreActions, useStoreState } from 'easy-peasy';

import client from '../../utils/apolloClient';

const searchPlacesQuery = gql`
  query searchPlacesQuery($input: SearchInput) {
    search_places(input: $input) {
      total_pages
      total_size
      place_listing {
        place_id
        status
        name
        address_line_1
        address_line_2
        city
        state
        country
        post_code
        location
        timezone
      }
    }
  }
`;

const fetchPlaces = async ({ input }) => {
  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchPlacesQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (data?.data?.search_places?.place_listing?.[0]) {
      return data.data.search_places.place_listing;
    }

    return [];
  } catch {
    return [];
  }
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const SearchBox = styled.div`
  position: relative;
  background: #f6f6f6;
  padding: 20px;
  width: 400px;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #e5e5e5;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;

  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 18px;
  cursor: pointer;

  &:hover {
    color: #00aeef;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  background: #f6f6f6;
  color: black;
  border: 1px solid #e5e5e5;
  outline: none;

  &:focus {
    border-color: #00aeef;
    box-shadow: 0 0 0 1px #00aeef;
  }
`;

const ResultsList = styled.ul`
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
`;

const ResultItem = styled.li`
  padding: 8px;
  cursor: pointer;
  color: black;

  &:hover,
  &.active {
    background: #00aeef;
    color: #f6f6f6;
  }
`;

const PlaceSearchOverlay = ({ isOpen, setIsOpen }) => {
  const { userId } = useStoreState(state => state.auth);
  const { setDefaultPlaceId } = useStoreActions(state => state.menu);

  const { defaultPlace } = useStoreState(state => state.menu);

  const [query, setQuery] = useState(() => defaultPlace?.name || '');
  const [touched, setTouched] = useState(false);

  const debounceRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState([]);

  const searchPlacesQueryInputFunc = value => ({
    user_id: userId,
    from: 0,
    size: 10,
    filter: {
      place_filter: {
        status: 'ACTIVE',
        place_name: value,
        has_service_type_setting: true,
        listing_status: 'APPROVED',
        claim_status: 'CLAIMED',
      },
      service_type_setting_filter: {
        status: 'ACTIVE',
      },
    },
  });

  return (
    <>
      <button onClick={() => setIsOpen(true)}>Select default place</button>

      {isOpen && (
        <Overlay>
          <SearchBox>
            <CloseButton onClick={() => setIsOpen(false)}>✕</CloseButton>

            <br />

            <SearchInput
              placeholder="Search places..."
              value={query}
              onChange={e => {
                const { value } = e.target;
                setTouched(true);
                setQuery(value);
                setLoading(true);
                clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(async () => {
                  const temp = await fetchPlaces({
                    input: searchPlacesQueryInputFunc(value),
                  });
                  setPlaces(temp);
                  setLoading(false);
                }, 300);
              }}
            />
            {touched && (
              <>
                {loading ? (
                  <ResultsList>
                    <ResultItem>loading...</ResultItem>
                  </ResultsList>
                ) : (
                  <ResultsList>
                    {places.map(place => (
                      <ResultItem
                        key={place.place_id}
                        onClick={() => {
                          setDefaultPlaceId(place.place_id);
                          setIsOpen(false);
                        }}
                      >
                        {place.name}
                        <div style={{ fontSize: '0.8em', opacity: 0.8 }}>
                          {[
                            place.address_line_1,
                            place.city,
                            place.state,
                            place.country,
                            place.post_code,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      </ResultItem>
                    ))}
                    {places.length === 0 && <ResultItem>No results</ResultItem>}
                  </ResultsList>
                )}
              </>
            )}
          </SearchBox>
        </Overlay>
      )}
    </>
  );
};

export default PlaceSearchOverlay;
